import './ffs-parameter-input.css'

import {
  CheckCircleTwoTone,
  InfoCircleOutlined,
  StopTwoTone,
  TableOutlined,
} from '@ant-design/icons'
import {
  Button,
  ConfigProvider,
  InputNumber,
  Select,
  Tooltip,
  Typography,
} from 'antd'
import { useGeckoLocale } from 'features/locale'
import isNil from 'lodash/isNil'
import React, {
  CSSProperties,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { LABEL_WIDTH } from './FfsCalcLayout'

export type MeasurementType =
  | 'thickness'
  | 'ratio'
  | 'pressure'
  | 'temperature'
  | 'length'
  | 'rate'
  | 'time'

const { Text } = Typography

const ICON_STYLE: React.CSSProperties = {
  fontSize: '1.5rem',
}

const RenderAfterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  height: 100%;
  flex-wrap: nowrap;
`

const UNITS: Partial<Record<MeasurementType, readonly GeckoMeasurementUnit[]>> =
  {
    thickness: [
      GeckoMeasurementUnit.Inches,
      GeckoMeasurementUnit.Feet,
      GeckoMeasurementUnit.Millimeters,
      GeckoMeasurementUnit.Meters,
    ],
    pressure: [
      GeckoMeasurementUnit.Megapascals,
      GeckoMeasurementUnit.PoundsForcePerSqInch,
    ],
    temperature: [
      GeckoMeasurementUnit.Fahrenheit,
      GeckoMeasurementUnit.Celsius,
    ],
    length: [
      GeckoMeasurementUnit.Millimeters,
      GeckoMeasurementUnit.Inches,
      GeckoMeasurementUnit.Feet,
      GeckoMeasurementUnit.Meters,
    ],
    rate: [
      GeckoMeasurementUnit.MillimetersPerYear,
      GeckoMeasurementUnit.InchesPerYear,
    ],
    time: [GeckoMeasurementUnit.Years],
  }

export interface FfsParameterInputFieldProps {
  label: ReactNode
  labelTooltip?: ReactNode
  measurementType?: MeasurementType
  labelWidth?: CSSProperties['width']
  value: GeckoMeasurement | null
  setValue?: Dispatch<SetStateAction<GeckoMeasurement | null>>
  isValid?: boolean
  min?: GeckoMeasurement
  max?: GeckoMeasurement
  step?: number
  trailingText?:
    | string
    | ((value: GeckoMeasurement | null) => string | undefined)
  onLookup?: () => void
  onInfo?: () => void
}

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  justify-content: left;
  width: 100%;
  aligh-items: center;
  gap: 1rem;
`

const RawFfsParameterField: FC<FfsParameterInputFieldProps> = ({
  label,
  labelTooltip,
  measurementType = 'thickness',
  labelWidth = LABEL_WIDTH,
  value,
  setValue,
  isValid,
  min,
  max,
  step,
  onLookup,
  onInfo,
  trailingText,
}) => {
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const [unit, setUnit] = useState<GeckoMeasurementUnit | null>(null)
  const allowableUnits = useMemo(
    () => UNITS[measurementType] ?? [],
    [measurementType]
  )
  useEffect(() => {
    if (allowableUnits.length) {
      setUnit(getPreferredMeasurementUnit(allowableUnits[0]))
    }
  }, [allowableUnits, getPreferredMeasurementUnit, measurementType])

  const unitOptions = useMemo(
    () =>
      allowableUnits.map((unit) => ({
        value: unit.id,
        label: unit.name,
      })),
    [allowableUnits]
  )

  const infoAfter = useMemo(
    () =>
      onInfo && (
        <Button
          shape='circle'
          icon={<InfoCircleOutlined />}
          onClick={onInfo}
          size='small'
          type='text'
        />
      ),
    [onInfo]
  )

  const lookupAfter = useMemo(
    () =>
      onLookup && (
        <Button
          shape='circle'
          icon={<TableOutlined />}
          onClick={onLookup}
          size='small'
          type='text'
        />
      ),
    [onLookup]
  )
  const selectAfter = useMemo(
    () =>
      unit && (
        <Select
          value={unit.id}
          options={unitOptions}
          onChange={(value) => setUnit(GeckoMeasurementUnit.GetByID(value)!)}
          style={{ width: 80, height: '100%' }}
          size='small'
        />
      ),
    [unit, unitOptions]
  )

  const trailingTextVal = useMemo(
    () =>
      typeof trailingText === 'function'
        ? trailingText(value?.convertTo(unit ?? value.unit) ?? null)
        : trailingText,
    [trailingText, unit, value]
  )

  const renderAfter = useMemo(() => {
    if (!infoAfter && !lookupAfter && !selectAfter && !trailingTextVal) {
      return undefined
    }
    return (
      <RenderAfterContainer>
        {trailingTextVal ? (
          <Text
            style={{
              color: CantileverTheme.colors.grayscale[300],
              fontSize: '0.7rem',
              whiteSpace: 'nowrap',
              marginLeft: '0.25rem',
              marginRight: '0.25rem',
            }}>
            {trailingTextVal}
          </Text>
        ) : undefined}
        {infoAfter ? infoAfter : undefined}
        {lookupAfter ? lookupAfter : undefined}
        {selectAfter ? selectAfter : undefined}
      </RenderAfterContainer>
    )
  }, [infoAfter, lookupAfter, selectAfter, trailingTextVal])

  const checkNewValue = useCallback(
    (value: GeckoMeasurement | null) => {
      if (value === null) {
        return true
      }
      if (min && min.convertTo(value.unit).num > value.num) {
        return false
      }
      if (max && max.convertTo(value.unit).num < value.num) {
        return false
      }
      return true
    },
    [max, min]
  )

  return (
    <ConfigProvider
      theme={{
        components: {
          InputNumber: {
            colorBgContainer:
              setValue || onLookup
                ? CantileverTheme.colors.grayscale[600]
                : CantileverTheme.colors.grayscale[700],
          },
        },
      }}>
      <Container>
        <Tooltip title={labelTooltip}>
          <Text style={{ width: labelWidth ?? '30%' }}>{label}</Text>
        </Tooltip>
        <InputNumber
          style={{ width: '70%' }}
          value={
            isNil(value)
              ? null
              : +value?.convertTo(unit ?? value.unit).num.toFixed(2)
          }
          onChange={(num) => {
            if (!setValue) {
              return
            }
            const newValue =
              num === null
                ? null
                : GeckoMeasurement.CreateFromValue(
                    num,
                    unit ?? value?.unit ?? GeckoMeasurementUnit.Millimeters
                  )
            if (checkNewValue(newValue)) {
              setValue(newValue)
            }
          }}
          readOnly={!setValue}
          addonAfter={renderAfter}
          status={isValid === false ? 'error' : undefined}
          step={step}
        />
        {isValid !== undefined && (
          <>
            {isValid ? (
              <CheckCircleTwoTone twoToneColor={'green'} style={ICON_STYLE} />
            ) : (
              <StopTwoTone twoToneColor={'red'} style={ICON_STYLE} />
            )}
          </>
        )}
      </Container>
    </ConfigProvider>
  )
}
RawFfsParameterField.displayName = 'FfsParameterField'

export const FfsParameterField = React.memo(RawFfsParameterField)
